import { Card } from 'components/common';
import { FormActionsStyle } from 'components/common/Forms/Forms.styled';
import { CardLoader } from 'components/common/Loading';
import React, { useEffect, useState } from 'react';
import {
  PopupDesc,
  PopupIconWrapper,
  PopupTitle,
} from './ConfirmDialog.styled';
import { useCommonTranslation } from '../../../hooks/i18n/useCommonTranslation';

interface InlineConfirmCardProps {
  icon?: React.ReactNode;
  title?: string;
  isLoading?: boolean;
  description?: string | React.ReactNode;
  status?: 'delete' | 'success' | 'warning';
  actions: React.ReactNode[];
  inlineIcon?: boolean;
  compact?: boolean;
  actionsTopMargin?: boolean;
  ariaLabel?: string;
}

const ARIA_LABEL_DELAY = 100;

/**
 * Use it as ariaLabel when you want to omit aria-label on inline confirm element.
 */
export const ARIA_LABEL_OMIT =
  'ARIA_LABEL_OMIT_ed1a7157-dad2-4163-845f-55f30afb73bc';

export const InlineConfirm: React.FunctionComponent<InlineConfirmCardProps> = ({
  icon,
  title,
  description,
  isLoading,
  status,
  actions,
  inlineIcon,
  children,
  actionsTopMargin,
  ariaLabel,
}) => {
  const { t } = useCommonTranslation();
  // The aria-label is set with a delay to ensure that screen readers announce the content of the dialog correctly.
  const [delayedAriaLabel, setDelayedAriaLabel] = useState<
    string | undefined
  >();

  const withAriaLabel = ariaLabel !== ARIA_LABEL_OMIT;
  useEffect(() => {
    if (withAriaLabel) {
      const timeoutId = setTimeout(() => {
        setDelayedAriaLabel(
          ariaLabel ?? title ?? t('confirm-dialog-default-aria-label'),
        );
      }, ARIA_LABEL_DELAY);
      return () => clearTimeout(timeoutId);
    }
  }, [withAriaLabel, ariaLabel, title, t]);

  return (
    <Card
      {...(withAriaLabel
        ? {
            role: 'dialog',
            'aria-live': 'polite',
            'aria-label': delayedAriaLabel,
          }
        : undefined)}
    >
      {Boolean(icon) && (
        <PopupIconWrapper inlineIcon={inlineIcon} status={status}>
          {icon}
        </PopupIconWrapper>
      )}
      {Boolean(title) && <PopupTitle compact>{title}</PopupTitle>}
      {Boolean(description) && <PopupDesc compact>{description}</PopupDesc>}
      {children}
      <FormActionsStyle align="center" topMargin={actionsTopMargin}>
        {actions && actions.map((node) => node)}
      </FormActionsStyle>
      {isLoading && <CardLoader fillWrapper={true} />}
    </Card>
  );
};
